// react_frontend/src/Context/MotherContext.js
import React, { createContext, useState, useCallback, useEffect, useRef } from "react";
import { AxiosAuthInstance } from "../AxiosInterceptors";

export const MotherContext = createContext();

export const MotherProvider = ({ children }) => {
  const [user, setUser] = useState({
    name: localStorage.getItem("name") || "User",
    role: localStorage.getItem("who_am_i") || "",
  });

  const [company, setCompany] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    mushak_number: "",
    vat: "",
    currency: "BDT",
    point: "",
    logo: "",
    package: "Trial",
    is_trial_active: true,
  });

  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const toggleSidebar = () => setSidebarOpen(!isSidebarOpen);

  // -------------------- Profile (NO POLLING) --------------------
  const fetchProfile = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return null;

    try {
      const res = await AxiosAuthInstance.get("company/profile");
      const companyData = res.data.company || {};
      const userData = res.data.user || {};

      const formattedCompany = {
        name: companyData.company_name || "",
        email: companyData.company_email || "",
        phone: companyData.company_phone || "",
        address: companyData.company_address || "",
        mushak_number: companyData.mushak_number || "",
        vat: companyData.vat || "",
        currency: companyData.currency || "BDT",
        point: companyData.point || "",
        logo: companyData.company_logo || "",
        package: !companyData.package ? "Trial" : companyData.package,
        is_trial_active: !companyData.package,
      };

      const formattedUser = {
        name: userData.name || "User",
        role: userData.who_am_i || "",
      };

      setUser(formattedUser);
      setCompany(formattedCompany);

      localStorage.setItem("name", formattedUser.name);
      localStorage.setItem("who_am_i", formattedUser.role);
      localStorage.setItem("package", formattedCompany.package);

      return formattedCompany;
    } catch (err) {
      console.error("Failed to fetch profile", err);
      return null;
    }
  }, []);

  // -------------------- Users --------------------
  const [users, setUsers] = useState([]);
  const pollingRef = useRef(null);
  const DEFAULT_INTERVAL = 15000;
  const MAX_INTERVAL = 60000;
  const intervalRef = useRef(DEFAULT_INTERVAL);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await AxiosAuthInstance.get("users");
      const newUsers = res.data.users || [];
      setUsers((prev) =>
        JSON.stringify(prev) !== JSON.stringify(newUsers) ? newUsers : prev
      );
      intervalRef.current = DEFAULT_INTERVAL;
    } catch (err) {
      console.error("Failed to fetch users", err);
      intervalRef.current = Math.min(intervalRef.current * 2, MAX_INTERVAL);
    } finally {
      if (pollingRef.current) clearTimeout(pollingRef.current);
      pollingRef.current = setTimeout(fetchUsers, intervalRef.current);
    }
  }, []);

  const addUserToList = (newUser) => setUsers((prev) => [...prev, newUser]);
  const updateUserInList = (updatedUser) =>
    setUsers((prev) => prev.map((u) => (u.id === updatedUser.id ? updatedUser : u)));
  const removeUserFromList = (userId) =>
    setUsers((prev) => prev.filter((u) => u.id !== userId));

  const startUserPolling = useCallback(() => {
    stopUserPolling();
    intervalRef.current = DEFAULT_INTERVAL;
    fetchUsers();
  }, [fetchUsers]);

  const stopUserPolling = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  // -------------------- Categories --------------------
  const [categories, setCategories] = useState([]);
  const catPollingRef = useRef(null);
  const catIntervalRef = useRef(DEFAULT_INTERVAL);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await AxiosAuthInstance.get("add_category");
      const newCategories = res.data.category || [];
      setCategories(newCategories);
      catIntervalRef.current = DEFAULT_INTERVAL;
    } catch (err) {
      console.error("Failed to fetch categories", err);
      catIntervalRef.current = Math.min(catIntervalRef.current * 2, MAX_INTERVAL);
    } finally {
      if (catPollingRef.current) clearTimeout(catPollingRef.current);
      catPollingRef.current = setTimeout(fetchCategories, catIntervalRef.current);
    }
  }, []);

  const addCategoryToList = (newCategory) =>
    setCategories((prev) => [...prev, newCategory]);

  const updateCategoryInList = (updatedCategory) =>
    setCategories((prev) =>
      prev.map((c) => (c.id === updatedCategory.id ? updatedCategory : c))
    );

  const removeCategoryFromList = (categoryId) =>
    setCategories((prev) => prev.filter((c) => c.id !== categoryId));

  const startCategoryPolling = useCallback(() => {
    stopCategoryPolling();
    catIntervalRef.current = DEFAULT_INTERVAL;
    fetchCategories();
  }, [fetchCategories]);

  const stopCategoryPolling = useCallback(() => {
    if (catPollingRef.current) {
      clearTimeout(catPollingRef.current);
      catPollingRef.current = null;
    }
  }, []);

  // -------------------- Items --------------------
  const [items, setItems] = useState([]);
  const itemPollingRef = useRef(null);
  const itemIntervalRef = useRef(DEFAULT_INTERVAL);
  const [loadingItems, setLoadingItems] = useState(true);
  const initialItemsLoad = useRef(true);

  const fetchItems = useCallback(async () => {
    if (initialItemsLoad.current) {
      setLoadingItems(true);
    }

    try {
      const res = await AxiosAuthInstance.get("add_item");
      const newItems = res.data.item || [];
      setItems(newItems);
      itemIntervalRef.current = DEFAULT_INTERVAL;
      initialItemsLoad.current = false;
    } catch (err) {
      console.error("Failed to fetch items", err);
      itemIntervalRef.current = Math.min(itemIntervalRef.current * 2, MAX_INTERVAL);
    } finally {
      if (!initialItemsLoad.current) {
        setLoadingItems(false);
      }
      if (itemPollingRef.current) clearTimeout(itemPollingRef.current);
      itemPollingRef.current = setTimeout(fetchItems, itemIntervalRef.current);
    }
  }, []);

  const startItemPolling = useCallback(() => {
    stopItemPolling();
    itemIntervalRef.current = DEFAULT_INTERVAL;
    fetchItems();
  }, [fetchItems]);

  const stopItemPolling = useCallback(() => {
    if (itemPollingRef.current) {
      clearTimeout(itemPollingRef.current);
      itemPollingRef.current = null;
    }
  }, []);

  const addItemToList = (newItem) => setItems((prev) => [...prev, newItem]);
  const updateItemInList = (updatedItem) =>
    setItems((prev) => prev.map((i) => (i.id === updatedItem.id ? updatedItem : i)));
  const removeItemFromList = (itemId) =>
    setItems((prev) => prev.filter((i) => i.id !== itemId));

  // -------------------- Products --------------------
  const [products, setProducts] = useState([]);
  const productPollingRef = useRef(null);
  const productIntervalRef = useRef(DEFAULT_INTERVAL);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const initialProductsLoad = useRef(true);

  const fetchProducts = useCallback(async () => {
    if (initialProductsLoad.current) {
      setLoadingProducts(true);
    }

    try {
      const res = await AxiosAuthInstance.get("add_product");
      const newProducts = res.data.product || [];
      setProducts(newProducts);
      productIntervalRef.current = DEFAULT_INTERVAL;
      initialProductsLoad.current = false;
    } catch (err) {
      console.error("Failed to fetch products", err);
      productIntervalRef.current = Math.min(productIntervalRef.current * 2, MAX_INTERVAL);
    } finally {
      if (!initialProductsLoad.current) {
        setLoadingProducts(false);
      }
      if (productPollingRef.current) clearTimeout(productPollingRef.current);
      productPollingRef.current = setTimeout(fetchProducts, productIntervalRef.current);
    }
  }, []);

  const startProductPolling = useCallback(() => {
    stopProductPolling();
    productIntervalRef.current = DEFAULT_INTERVAL;
    fetchProducts();
  }, [fetchProducts]);

  const stopProductPolling = useCallback(() => {
    if (productPollingRef.current) {
      clearTimeout(productPollingRef.current);
      productPollingRef.current = null;
    }
  }, []);

  const addProductToList = (newProduct) => setProducts((prev) => [...prev, newProduct]);
  const updateProductInList = (updatedProduct) =>
    setProducts((prev) => prev.map((p) => (p.id === updatedProduct.id ? updatedProduct : p)));
  const removeProductFromList = (productId) =>
    setProducts((prev) => prev.filter((p) => p.id !== productId));

  // -------------------- Inventory --------------------
  const [inventory, setInventory] = useState([]);
  const invPollingRef = useRef(null);
  const invIntervalRef = useRef(DEFAULT_INTERVAL);
  const [loadingInventory, setLoadingInventory] = useState(true);
  const initialInventoryLoad = useRef(true);

  const fetchInventory = useCallback(async () => {
    if (initialInventoryLoad.current) {
      setLoadingInventory(true);
    }

    try {
      const res = await AxiosAuthInstance.get("inventories");
      const newInventory = res.data.transactions || [];
      setInventory(newInventory);
      invIntervalRef.current = DEFAULT_INTERVAL;
      initialInventoryLoad.current = false;
    } catch (err) {
      console.error("Failed to fetch inventory", err);
      invIntervalRef.current = Math.min(invIntervalRef.current * 2, MAX_INTERVAL);
    } finally {
      if (!initialInventoryLoad.current) {
        setLoadingInventory(false);
      }
      if (invPollingRef.current) clearTimeout(invPollingRef.current);
      invPollingRef.current = setTimeout(fetchInventory, invIntervalRef.current);
    }
  }, []);

  const startInventoryPolling = useCallback(() => {
    stopInventoryPolling();
    invIntervalRef.current = DEFAULT_INTERVAL;
    fetchInventory();
  }, [fetchInventory]);

  const stopInventoryPolling = useCallback(() => {
    if (invPollingRef.current) {
      clearTimeout(invPollingRef.current);
      invPollingRef.current = null;
    }
  }, []);

  const updateInventoryInList = (updatedItem) =>
    setInventory((prev) => prev.map((i) => (i.id === updatedItem.id ? updatedItem : i)));

  // -------------------- Customers --------------------
  const [customers, setCustomers] = useState([]);
  const custPollingRef = useRef(null);
  const custIntervalRef = useRef(DEFAULT_INTERVAL);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const initialCustomersLoad = useRef(true);

  const fetchCustomers = useCallback(async () => {
    if (initialCustomersLoad.current) {
      setLoadingCustomers(true);
    }
    try {
      const res = await AxiosAuthInstance.get("customers");
      const newCustomers = res.data.customers || [];
      setCustomers(newCustomers);
      custIntervalRef.current = DEFAULT_INTERVAL;
      initialCustomersLoad.current = false;
    } catch (err) {
      console.error("Failed to fetch customers", err);
      custIntervalRef.current = Math.min(custIntervalRef.current * 2, MAX_INTERVAL);
    } finally {
      if (!initialCustomersLoad.current) {
        setLoadingCustomers(false);
      }
      if (custPollingRef.current) clearTimeout(custPollingRef.current);
      custPollingRef.current = setTimeout(fetchCustomers, custIntervalRef.current);
    }
  }, []);

  const startCustomerPolling = useCallback(() => {
    stopCustomerPolling();
    custIntervalRef.current = DEFAULT_INTERVAL;
    fetchCustomers();
  }, [fetchCustomers]);

  const stopCustomerPolling = useCallback(() => {
    if (custPollingRef.current) {
      clearTimeout(custPollingRef.current);
      custPollingRef.current = null;
    }
  }, []);

  const addCustomerToList = (newCustomer) => setCustomers((prev) => [...prev, newCustomer]);
  const updateCustomerInList = (updatedCustomer) =>
    setCustomers((prev) => prev.map((c) => (c.id === updatedCustomer.id ? updatedCustomer : c)));
  const removeCustomerFromList = (customerId) =>
    setCustomers((prev) => prev.filter((c) => c.id !== customerId));

  // -------------------- Employees --------------------
  const [employees, setEmployees] = useState([]);
  const empPollingRef = useRef(null);
  const empIntervalRef = useRef(DEFAULT_INTERVAL);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const initialEmployeesLoad = useRef(true);

  const fetchEmployees = useCallback(async () => {
    if (initialEmployeesLoad.current) {
      setLoadingEmployees(true);
    }

    try {
      const res = await AxiosAuthInstance.get("add_employee");
      const newEmployees = res.data.employee || [];
      setEmployees(newEmployees);
      empIntervalRef.current = DEFAULT_INTERVAL;
      initialEmployeesLoad.current = false;
    } catch (err) {
      console.error("Failed to fetch employees", err);
      empIntervalRef.current = Math.min(empIntervalRef.current * 2, MAX_INTERVAL);
    } finally {
      if (!initialEmployeesLoad.current) {
        setLoadingEmployees(false);
      }
      if (empPollingRef.current) clearTimeout(empPollingRef.current);
      empPollingRef.current = setTimeout(fetchEmployees, empIntervalRef.current);
    }
  }, []);

  const startEmployeePolling = useCallback(() => {
    stopEmployeePolling();
    empIntervalRef.current = DEFAULT_INTERVAL;
    fetchEmployees();
  }, [fetchEmployees]);

  const stopEmployeePolling = useCallback(() => {
    if (empPollingRef.current) {
      clearTimeout(empPollingRef.current);
      empPollingRef.current = null;
    }
  }, []);

  const addEmployeeToList = (newEmployee) => setEmployees((prev) => [...prev, newEmployee]);
  const updateEmployeeInList = (updatedEmployee) =>
    setEmployees((prev) => prev.map((e) => (e.id === updatedEmployee.id ? updatedEmployee : e)));
  const removeEmployeeFromList = (employeeId) =>
    setEmployees((prev) => prev.filter((e) => e.id !== employeeId));

  // -------------------- Salaries --------------------
  const [salaries, setSalaries] = useState([]);
  const salPollingRef = useRef(null);
  const salIntervalRef = useRef(DEFAULT_INTERVAL);
  const [loadingSalaries, setLoadingSalaries] = useState(true);
  const initialSalariesLoad = useRef(true);

  const fetchSalaries = useCallback(async () => {
    if (initialSalariesLoad.current) {
      setLoadingSalaries(true);
    }

    try {
      const res = await AxiosAuthInstance.get("add_salary");
      const newSalaries = res.data.employeeSalary || [];
      setSalaries(newSalaries);
      salIntervalRef.current = DEFAULT_INTERVAL;
      initialSalariesLoad.current = false;
    } catch (err) {
      console.error("Failed to fetch salaries", err);
      salIntervalRef.current = Math.min(salIntervalRef.current * 2, MAX_INTERVAL);
    } finally {
      if (!initialSalariesLoad.current) {
        setLoadingSalaries(false);
      }
      if (salPollingRef.current) clearTimeout(salPollingRef.current);
      salPollingRef.current = setTimeout(fetchSalaries, salIntervalRef.current);
    }
  }, []);

  const startSalaryPolling = useCallback(() => {
    stopSalaryPolling();
    salIntervalRef.current = DEFAULT_INTERVAL;
    fetchSalaries();
  }, [fetchSalaries]);

  const stopSalaryPolling = useCallback(() => {
    if (salPollingRef.current) {
      clearTimeout(salPollingRef.current);
      salPollingRef.current = null;
    }
  }, []);

  const addSalaryToList = (newSalary) => setSalaries((prev) => [...prev, newSalary]);
  const updateSalaryInList = (updatedSalary) =>
    setSalaries((prev) => prev.map((s) => (s.id === updatedSalary.id ? updatedSalary : s)));
  const removeSalaryFromList = (salaryId) =>
    setSalaries((prev) => prev.filter((s) => s.id !== salaryId));

  // -------------------- Purchases --------------------  
  const [purchases, setPurchases] = useState([]);
  const purchasePollingRef = useRef(null);
  const purchaseIntervalRef = useRef(DEFAULT_INTERVAL);
  const [loadingPurchases, setLoadingPurchases] = useState(true);
  const initialPurchasesLoad = useRef(true);

  const fetchPurchases = useCallback(async () => {
    if (initialPurchasesLoad.current) {
      setLoadingPurchases(true);
    }

    try {
      const res = await AxiosAuthInstance.get("purchases");
      const newPurchases = res.data.purchases || [];
      setPurchases(newPurchases);
      purchaseIntervalRef.current = DEFAULT_INTERVAL;
      initialPurchasesLoad.current = false;
    } catch (err) {
      console.error("Failed to fetch purchases", err);
      purchaseIntervalRef.current = Math.min(purchaseIntervalRef.current * 2, MAX_INTERVAL);
    } finally {
      if (!initialPurchasesLoad.current) {
        setLoadingPurchases(false);
      }
      if (purchasePollingRef.current) clearTimeout(purchasePollingRef.current);
      purchasePollingRef.current = setTimeout(fetchPurchases, purchaseIntervalRef.current);
    }
  }, []);

  const startPurchasePolling = useCallback(() => {
    stopPurchasePolling();
    purchaseIntervalRef.current = DEFAULT_INTERVAL;
    fetchPurchases();
  }, [fetchPurchases]);

  const stopPurchasePolling = useCallback(() => {
    if (purchasePollingRef.current) {
      clearTimeout(purchasePollingRef.current);
      purchasePollingRef.current = null;
    }
  }, []);

  const addPurchaseToList = (newPurchase) => setPurchases((prev) => [...prev, newPurchase]);
  const updatePurchaseInList = (updatedPurchase) =>
    setPurchases((prev) => prev.map((p) => (p.id === updatedPurchase.id ? updatedPurchase : p)));
  const removePurchaseFromList = (purchaseId) =>
    setPurchases((prev) => prev.filter((p) => p.id !== purchaseId));

  // -------------------- Billings --------------------
  const [billings, setBillings] = useState([]);
  const billingPollingRef = useRef(null);
  const billingIntervalRef = useRef(DEFAULT_INTERVAL);
  const [loadingBillings, setLoadingBillings] = useState(true);
  const initialBillingsLoad = useRef(true);

  const fetchBillings = useCallback(async () => {
    if (initialBillingsLoad.current) {
      setLoadingBillings(true);
    }

    try {
      const res = await AxiosAuthInstance.get("billings");
      const newBillings = res.data.billings || [];
      setBillings(newBillings);
      billingIntervalRef.current = DEFAULT_INTERVAL;
      initialBillingsLoad.current = false;
    } catch (err) {
      console.error("Failed to fetch billings", err);
      billingIntervalRef.current = Math.min(billingIntervalRef.current * 2, MAX_INTERVAL);
    } finally {
      if (!initialBillingsLoad.current) {
        setLoadingBillings(false);
      }
      if (billingPollingRef.current) clearTimeout(billingPollingRef.current);
      billingPollingRef.current = setTimeout(fetchBillings, billingIntervalRef.current);
    }
  }, []);

  const startBillingPolling = useCallback(() => {
    stopBillingPolling();
    billingIntervalRef.current = DEFAULT_INTERVAL;
    fetchBillings();
  }, [fetchBillings]);

  const stopBillingPolling = useCallback(() => {
    if (billingPollingRef.current) {
      clearTimeout(billingPollingRef.current);
      billingPollingRef.current = null;
    }
  }, []);

  const addBillingToList = (newBilling) => setBillings((prev) => [...prev, newBilling]);
  const updateBillingInList = (updatedBilling) =>
    setBillings((prev) => prev.map((b) => (b.id === updatedBilling.id ? updatedBilling : b)));
  const removeBillingFromList = (billingId) =>
    setBillings((prev) => prev.filter((b) => b.id !== billingId));

  const updateUser = (newUser) => {
    setUser((prev) => ({ ...prev, ...newUser }));
    if (newUser.name) localStorage.setItem("name", newUser.name);
    if (newUser.role) localStorage.setItem("who_am_i", newUser.role);
  };

  const updateCompany = (newCompany) => {
    setCompany((prev) => ({ ...prev, ...newCompany }));
    if (newCompany.package) localStorage.setItem("package", newCompany.package);
  };

  return (
    <MotherContext.Provider
      value={{
        // profile
        user,
        company,
        fetchProfile,
        updateUser,
        updateCompany,
        isSidebarOpen,
        toggleSidebar,
        // categories
        categories,
        fetchCategories,
        addCategoryToList,
        updateCategoryInList,
        removeCategoryFromList,
        startCategoryPolling,
        stopCategoryPolling,
        // items
        items,
        loadingItems,
        fetchItems,
        addItemToList,
        updateItemInList,
        removeItemFromList,
        startItemPolling,
        stopItemPolling,
        // products
        products,
        loadingProducts,
        fetchProducts,
        addProductToList,
        updateProductInList,
        removeProductFromList,
        startProductPolling,
        stopProductPolling,
        // users
        users,
        fetchUsers,
        addUserToList,
        updateUserInList,
        removeUserFromList,
        startUserPolling,
        stopUserPolling,
        // inventory
        inventory,
        loadingInventory,
        fetchInventory,
        updateInventoryInList,
        startInventoryPolling,
        stopInventoryPolling,
        // customers
        customers,
        loadingCustomers,
        fetchCustomers,
        addCustomerToList,
        updateCustomerInList,
        removeCustomerFromList,
        startCustomerPolling,
        stopCustomerPolling,
        // employees
        employees,
        loadingEmployees,
        fetchEmployees,
        addEmployeeToList,
        updateEmployeeInList,
        removeEmployeeFromList,
        startEmployeePolling,
        stopEmployeePolling,
        // salaries
        salaries,
        loadingSalaries,
        fetchSalaries,
        addSalaryToList,
        updateSalaryInList,
        removeSalaryFromList,
        startSalaryPolling,
        stopSalaryPolling,
        // billings
        billings,
        loadingBillings,
        fetchBillings,
        addBillingToList,
        updateBillingInList,
        removeBillingFromList,
        startBillingPolling,
        stopBillingPolling,
        // purchases
        purchases,
        loadingPurchases,
        fetchPurchases,
        addPurchaseToList,
        updatePurchaseInList,
        removePurchaseFromList,
        startPurchasePolling,
        stopPurchasePolling,
      }}
    >
      {children}
    </MotherContext.Provider>
  );
};